<script setup lang="ts">
import {computed, defineProps, onMounted, getCurrentInstance} from "vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import FilteredInputWrapper from "@/components/forms/payout/forms/inputs/FilteredInputWrapper.vue";
import PayoutFormGroup from "@/components/forms/payout/PayoutFormGroup.vue";
import PayoutFormGroupRegularWidth from "@/components/forms/payout/PayoutFormGroupRegularWidth.vue";
import PayoutCountriesDropdown
  from "@/components/forms/payout/forms/banks/PayoutCountriesDropdown.vue";
import EpayBankOptions from "@/components/forms/payout/forms/epay/EpayBankOptions.vue";
import PayoutMaskedInput2 from "@/components/forms/payout/forms/inputs/PayoutMaskedInput2.vue";
import EpayGenderOptions from "@/components/forms/payout/forms/epay/EpayGenderOptions.vue";
import EpayDocumentTypeOptions
  from "@/components/forms/payout/forms/epay/EpayDocumentTypeOptions.vue";
import PayoutDateInput from "@/components/forms/payout/forms/PayoutDateInput.vue";
import EpayOccupationOptions from "@/components/forms/payout/forms/epay/EpayOccupationOptions.vue";
import EpaySourceOfFundsOptions
  from "@/components/forms/payout/forms/epay/EpaySourceOfFundsOptions.vue";
import EpayBeneficiaryRelationshipOptions
  from "@/components/forms/payout/forms/epay/EpayBeneficiaryRelationshipOptions.vue";
import EpayPurposeOfRemittanceOptions
  from "@/components/forms/payout/forms/epay/EpayPurposeOfRemittanceOptions.vue";
import PayoutStatesDropdown from "@/components/forms/payout/forms/banks/PayoutStatesDropdown.vue";

const props = defineProps({
  model: {
    type: Object,
    required: true
  },
  attribute: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  required: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'text'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  datePickerOptions: {
    type: Object,
    default: () => ({})
  },
  hint: {
    type: String,
    default: ''
  },
  syncAttributes: {
    type: Array<string>,
    default: () => []
  },
  minAge: {
    type: Number,
    default: 18
  },
  countryValueKey: {
    type: String,
    default: 'alpha2'
  },
  countries: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: ''
  },
  stateCountry: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['input']);

const placeholder = computed(() => {
  return props.placeholder;
})

const datePickerOptionsDefaults = computed(() => {
  // minDate and maxDate +- 100 years from current date
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 15);
  return {
    range: false,
    dateFormat: 'yyyy-MM-dd',
    minDate,
    maxDate,
    //view: 'years', minView: 'months',
    autoClose: true,
    language: getCurrentInstance()?.proxy?.$i18n?.locale || 'en',
  }
})


const singleDatePickerOptionsDefaults = computed(() => {
  // minDate and maxDate +- 100 years from current date
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - props.minAge);
  return {
    range: false,
    dateFormat: 'yyyy-MM-dd',
    minDate,
    maxDate,
    //view: 'years', minView: 'months',
    autoClose: true,
    language: getCurrentInstance()?.proxy?.$i18n?.locale || 'en',
  }
})


const datePickerOptions = computed(() => {
  return {...datePickerOptionsDefaults.value, ...props.datePickerOptions}
})

const singleDatePickerOptions = computed(() => {
  return {...singleDatePickerOptionsDefaults.value, ...props.datePickerOptions}
})


const isCountryDropdown = computed(() => {
  return attribute.value.includes('country') || type.value === 'country';
})

const isStateDropdown = computed(() => {
  return /*attribute.value.includes('state') || */type.value === 'state';
})


const type = computed(() => {
  return props.type;
})

const isMalaysiaBank = computed(() => {
  return type.value === 'bank_malaysia_spayz';
})


const isIndiaBank = computed(() => {
  return type.value === 'bank_in';
})

const isIndonesiaBank = computed(() => {
  return type.value === 'bank_id';
})


const isPhoneWithArea = computed(() => {
  return type.value === 'phonewitharea';
})


const model = computed(() => {
  return props.model;
})

const label = computed(() => {
  return props.label;
})

const required = computed(() => {
  return props.required;
})

const isGenderDropdown = computed(() => {
  return type.value == 'gender';
})

const isDatePeriod = computed(() => {
  return type.value === 'period';
})


const isDate = computed(() => {
  return type.value === 'date';
})

const attribute = computed(() => {
  // if (isPhoneWithArea.value) {
  //   console.log('props.attribute', props.attribute);
  //   const arr = props.attribute?.split(',') || ['', ''];
  //   return arr[1]
  // }
  return props.attribute;
})

const areaphoneAttributeArea = computed(() => {
  const attrs = attribute.value?.split(',') || [];
  return attrs[0] || null;
})

const areaphoneAttributePhone = computed(() => {
  const attrs = attribute.value?.split(',') || [];
  return attrs[1] || null;
})

const periodAttributeBegin = computed(() => {
  const attrs = attribute.value?.split(',') || [];
  return attrs[0] || null;
})

const periodAttributeEnd = computed(() => {
  const attrs = attribute.value?.split(',') || [];
  return attrs[1] || null;
})

const isDocumentType = computed(() => {
  return type.value === 'idtype';
})

const isOccupation = computed(() => {
  return type.value === 'occupation';
})

const isSourceOfFunds = computed(() => {
  return type.value === 'source_of_funds';
})

const isBeneficiaryRelationship = computed(() => {
  return type.value === 'beneficiary_relationship';
})

const isPurposeOfRemittance = computed(() => {
  return type.value === 'purpose_of_remittance';
})

const value = computed({
  get: () => props.model.values[attribute.value] || '',
  set: (value) => {
    const data: Record<string, any> = {};
    if (props.syncAttributes.length > 0) {
      props.syncAttributes.forEach((attr: string) => {
        data[attr] = value;
      })
    }
    props.model.values = {...props.model.values, [attribute.value]: value, ...data}
    emit('input', value);
  }
})

const hint = computed(() => {
  if (props.hint) return props.hint;
  const $i18n = getCurrentInstance()?.proxy?.$i18n;
  if (isPhoneWithArea.value) {
    return $i18n?.t('phone_hint');
  }
  return props.hint || model.value.getHint(attribute.value);
})

const hasErrors = computed(() => {
  if (isPhoneWithArea.value) {
    return model.value.errors.has(areaphoneAttributeArea.value) || model.value.errors.has(areaphoneAttributePhone.value);
  }
  return model.value.errors.has(attribute.value);
})

const errorAttribute = computed(() => {
  if (isDatePeriod.value) {
    return periodAttributeBegin.value + '|' + periodAttributeEnd.value;
  }
  if (isPhoneWithArea.value) {
    return areaphoneAttributeArea.value +
      '|' + areaphoneAttributePhone.value;
  }
  return attribute.value;
})

const disabled = computed(() => {
  return props.disabled;
})

onMounted(() => {
  model.value[attribute.value] = value.value;
})
</script>

<template>
  <payout-form-group :label="label"
                     :required="required"
                     :has-errors="hasErrors">
    <template v-if="isStateDropdown">
      <PayoutFormGroupRegularWidth>
        <PayoutStatesDropdown
          :disabled="disabled"
          :country="stateCountry"
          :show-key-in-name="true"
          v-model="value"></PayoutStatesDropdown>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isCountryDropdown">
      <PayoutFormGroupRegularWidth>
        <PayoutCountriesDropdown
          :disabled="disabled"
          :value-key="countryValueKey"
          :limit="countries"
          :show-key-in-name="true"
          v-model="value"></PayoutCountriesDropdown>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isIndiaBank">
      <PayoutFormGroupRegularWidth>
        <EpayBankOptions
          v-model="value"
          country="india"
          :disabled="disabled"></EpayBankOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isIndonesiaBank">
      <PayoutFormGroupRegularWidth>
        <EpayBankOptions
          country="indonesia"
          v-model="value"
          :disabled="disabled"></EpayBankOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isDocumentType">
      <PayoutFormGroupRegularWidth>
        <EpayDocumentTypeOptions
          v-model="value"
          :disabled="disabled"></EpayDocumentTypeOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isDatePeriod">
      <PayoutFormGroupRegularWidth>
        <div class="date-period">
          <PayoutDateInput v-model="model.values[periodAttributeBegin]"
                           :picker-options="datePickerOptions"
                           :init-value="model.values[periodAttributeBegin]"
                           class="form-control"></PayoutDateInput>
          <div class="date-period--separator">
            &ndash;
          </div>
          <PayoutDateInput v-model="model.values[periodAttributeEnd]"
                           :picker-options="datePickerOptions"
                           :init-value="model.values[periodAttributeEnd]"
                           class="form-control"></PayoutDateInput>
        </div>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isPurposeOfRemittance">
      <PayoutFormGroupRegularWidth>
        <EpayPurposeOfRemittanceOptions
          v-model="value"
          :disabled="disabled"></EpayPurposeOfRemittanceOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isBeneficiaryRelationship">
      <PayoutFormGroupRegularWidth>
        <EpayBeneficiaryRelationshipOptions
          v-model="value"
          :disabled="disabled"></EpayBeneficiaryRelationshipOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isSourceOfFunds">
      <PayoutFormGroupRegularWidth>
        <EpaySourceOfFundsOptions
          v-model="value"
          :disabled="disabled"></EpaySourceOfFundsOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isOccupation">
      <PayoutFormGroupRegularWidth>
        <EpayOccupationOptions
          v-model="value"
          :disabled="disabled"></EpayOccupationOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isDate">
      <PayoutFormGroupRegularWidth>
        <PayoutDateInput v-model="value"
                         :picker-options="singleDatePickerOptions"
                         :init-value="value"
                         class="form-control"></PayoutDateInput>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isGenderDropdown">
      <PayoutFormGroupRegularWidth>
        <EpayGenderOptions
          v-model="value"
          :disabled="disabled"></EpayGenderOptions>
      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else-if="isPhoneWithArea">
<!--      <PayoutFormGroupRegularWidth>-->
<!--        <div class="phone__group" v-if="areaphoneAttributeArea && areaphoneAttributePhone">-->
<!--          <div class="area-input">-->
<!--            <PayoutMaskedInput2 v-model="model.values[areaphoneAttributeArea]" :disabled="disabled" mask="###" />-->
<!--          </div>-->
<!--            <PayoutMaskedInput2 v-model="model.values[areaphoneAttributePhone]" :disabled="disabled" mask="## #### ####" />-->
<!--        </div>-->
<!--      </PayoutFormGroupRegularWidth>-->
      <PayoutFormGroupRegularWidth>
        <div class="phone__group">
          <div class="phone__group__area phone__group__input_with_hint">
            <PayoutMaskedInput2 v-model="model.values[areaphoneAttributeArea]" :disabled="disabled" mask="###" />
            <div class="phone__group__hint">{{ $t('Код зоны') }}</div>
          </div>
          <div class="phone__group__input_with_hint">
            <PayoutMaskedInput2 v-model="model.values[areaphoneAttributePhone]" :disabled="disabled" mask="## #### ####" />
            <div class="phone__group__hint">{{ $t('Номер телефона') }}</div>
          </div>
        </div>

      </PayoutFormGroupRegularWidth>
    </template>
    <template v-else>
      <filtered-input-wrapper v-model="value" :placeholder="placeholder" :disabled="disabled"></filtered-input-wrapper>
    </template>
    <payout-input-hint-block v-slot:hints
                             :hint="hint"></payout-input-hint-block>
    <error-message-block v-slot:errors
                         :attribute="errorAttribute"
                         :errors="model.errors"></error-message-block>
  </payout-form-group>
</template>

<style scoped lang="less">
.phone__group {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  flex-direction: row;

  //display: flex;
  //align-items: center;
  //justify-content: flex-start;
  //gap: 15px;
  width: 100%;
  .area-input {
    width: 25%;
  }

  .phone__group__hint {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 5px;
    padding-left: 5px;
  }

  .phone__group__area.phone__group__input_with_hint {
    width: 25%;
    flex-shrink: 0;
    flex-grow: 0;
  }

  .phone__group__input_with_hint {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
}

.date-period {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 15px;
  width: 100%;
  .date-period--separator {

  }
}
/*

.phone-grid {
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-template-areas:
    "area number"
    "area-hint number-hint";
  gap: 5px 1.5rem;
}

.area-input {
  grid-area: area;
}

.number-input {
  grid-area: number;
}

.area-hint {
  grid-area: area-hint;
}

.number-hint {
  grid-area: number-hint;
}*/

</style>

<i18n>
{
  "en": {
    "phone_hint": "Country code and phone number.",
    "Номер телефона": "Phone number",
    "Код зоны": "Area code"
  },
  "ru": {
    "phone_hint": "Код страны и номер телефона."
  }
}
</i18n>
